import type { EditField } from '../pages/recordPages';
import { fill, t } from '../strings';
import { proposeSurvivor, type DuplicateRecordFacts, type DuplicateRecordType, type SurvivorFacts } from './duplicateReport';
import type { MergeRoute, MergeTarget } from './mergeMap';
import { mergeFieldFilled, mergeFields, type MergeFieldValues, type MergeMoveList } from './mergeStep';

/**
 * Merge the records selected on a list, two or more at once (piece 10;
 * decision 7450). The Companies and People lists carry Merge in their
 * selection bar; it opens the merge window of decision 2501 with a column per
 * selected record, and the press merges each other record into the one that
 * stays through the existing merge, one transaction each.
 */

/** 🔒 The most records one Merge on a list takes: the one that stays and nineteen merged into it (decision 7450). */
export const MERGE_SET_CAP = 20;

/** 🔒 The sentence a selection past the cap is refused with, built from the cap itself so the button's reason and the server's refusal read the same (T3095). */
export function mergeSetCapSentence(): string {
  return fill(t('duplicates.mergeSet.cap'), { count: String(MERGE_SET_CAP) });
}

/** The records a list's Merge takes: a company or a person (decision 7450), and an item (decision 7470). */
export type MergeSetRecordType = DuplicateRecordType | 'Item';

/** An item's own facts in its column's head (decision 7470): its type, its unit, what is on hand and its open document lines. */
export interface ItemMergeFacts {
  typeLabel: string;
  unitName: string;
  /** Across every site; null on a Service, which holds no stock. */
  onHand: number | null;
  openLines: number;
}

/** One selected record as the window draws its column: the report's facts and its values for the step's fields. */
export interface MergeSetRecord {
  facts: DuplicateRecordFacts;
  values: MergeFieldValues;
  /** A value as a person reads it, by field, where the stored one is an id or an amount: an item's category, a price (decision 7470). */
  labels?: Readonly<Record<string, string>>;
  /** An item's own facts, present on an item's column alone. */
  item?: ItemMergeFacts;
}

/** What moves from one merged record to the one that stays, read by the merge's own dry pass. */
export interface MergeSetMoves {
  losingId: string;
  moves: MergeMoveList[];
}

/** What the window reads before the press (decision 7450): every record's column, the survivor proposed, and what moves per merged record. */
export interface MergeSetPreviewAnswer {
  recordType: MergeSetRecordType;
  /** The selected records, in the order the list asked for them. */
  records: MergeSetRecord[];
  /** The one decision 1484's rule proposes across the whole set. */
  proposedSurvivorId: string;
  /** The one the moves were read against: the survivor asked for, else the one proposed. */
  survivorId: string;
  /** Each other record's moves, in the order they merge. */
  moves: MergeSetMoves[];
  /** The press raises a Merge Request rather than merging at once. */
  goesToApprover: boolean;
  /**
   * 🔒 The approvers the press goes to, one request each (decision 7450; found
   * in review, 2026-10-24): each pair routes as it would alone, so a set of
   * people both inside and outside the Organization goes to HR and to Sales.
   * Empty where the set merges at once.
   */
  approvalRoutes: MergeRoute[];
  /** Why the whole set cannot merge, in words, before the press: two items in different units (decision 7470). */
  refusal?: string | null;
  /** 🔒 The labels of the fields this viewer may not see (decision 0125), absent from every record and kept from the one that stays (decision 7470). */
  withheld?: string[];
}

/** One record the press merges away, with the fields whose value the one that stays takes from it. */
export interface MergeSetMember {
  id: string;
  fromMerged: string[];
}

/** How one pair of a set merged at once went: merged, or the refusal in words. */
export type MergeSetPairOutcome = { losingId: string; name: string; merged: true } | { losingId: string; name: string; merged: false; refusal: string };

/** One Merge Request a set's press raised, to the approver its pairs route to. */
export interface MergeSetRequest {
  requestId: string;
  route: MergeRoute;
  escalationReason: string | null;
}

/** What the set's press answers: each pair's outcome where it merged at once, or the requests it raised, one per approver. */
export type MergeSetAnswer =
  | { outcome: 'Merged'; survivorId: string; pairs: MergeSetPairOutcome[] }
  | { outcome: 'Requested'; requests: MergeSetRequest[] };

/** 🔒 The survivor decision 1484's rule proposes across a set, read pair by pair as Merge all exact reads a group. */
export function proposeSetSurvivor(facts: readonly SurvivorFacts[]): string {
  if (facts.length === 0) throw new Error('A set names at least one record.');
  return facts.reduce((kept, one) => (proposeSurvivor(kept, one) === kept.id ? kept : one)).id;
}

/** A field at least two of the set's records fill with different values: a row of the window. */
export interface MergeSetConflict {
  key: string;
  label: string;
  field: EditField;
  /** Each record's value, by its id; an empty value is present and takes no pick. */
  values: Readonly<Record<string, unknown>>;
}

/** The same comparison the pair's step makes: strings by their trimmed text, anything else by its whole shape. */
function valueKey(value: unknown): string {
  return typeof value === 'string' ? `s:${value.trim()}` : `j:${JSON.stringify(value)}`;
}

/** 🔒 The rows of the set's window (decision 7450): every step field at least two records fill with different values. */
export function mergeSetConflicts(recordType: MergeTarget, records: readonly { id: string; values: MergeFieldValues }[]): MergeSetConflict[] {
  return mergeFields(recordType)
    .filter((field) => new Set(records.filter((record) => mergeFieldFilled(record.values[field.key])).map((record) => valueKey(record.values[field.key]))).size > 1)
    .map((field) => ({ key: field.key, label: field.label, field, values: Object.fromEntries(records.map((record) => [record.id, record.values[field.key] ?? null])) }));
}

/** Whether a record's cell on a row holds a value a pick can take. */
export function mergeSetPickable(conflict: MergeSetConflict, recordId: string): boolean {
  return mergeFieldFilled(conflict.values[recordId]);
}

/**
 * 🔒 The column a row's pick starts on (decision 7450): the one that stays where
 * it fills the field, else the first record merged into it, in the merge's
 * order, that does.
 */
export function defaultSetPick(conflict: MergeSetConflict, survivorId: string, order: readonly string[]): string {
  if (mergeSetPickable(conflict, survivorId)) return survivorId;
  return order.find((id) => id !== survivorId && mergeSetPickable(conflict, id)) ?? survivorId;
}

/**
 * Each merged record's picks for the press: the fields whose picked column is
 * that record's. A row picked on the survivor rides no pair. The merges run in
 * order, so a record that fills a field the survivor leaves empty fills it
 * first and a later record's pick overwrites it, which is the pick's meaning.
 */
export function setPicksByRecord(picks: Readonly<Record<string, string>>, survivorId: string): Record<string, string[]> {
  const byRecord: Record<string, string[]> = {};
  for (const [key, id] of Object.entries(picks)) {
    if (id === survivorId) continue;
    (byRecord[id] ??= []).push(key);
  }
  return byRecord;
}

/**
 * 🔒 Why items cannot merge (decision 7470): two that count in different units,
 * named by their units, or null where every one counts in the same unit.
 */
export function itemUnitRefusal(unitNames: readonly string[]): string | null {
  const units = [...new Set(unitNames)];
  if (units.length < 2) return null;
  const named = units.length === 2 ? `${units[0]} and ${units[1]}` : `${units.slice(0, -1).join(', ')} and ${units[units.length - 1]}`;
  return `These items count in different units, ${named}, so they cannot be merged.`;
}

/**
 * 🔒 The refusal a press reads when it picks a field the presser may not see
 * (decisions 0125, 7470), naming each by the Item form's own label.
 */
export function itemWithheldPickSentence(keys: readonly string[]): string {
  const labels = keys.map((key) => mergeFields('Item').find((field) => field.key === key)?.label ?? key);
  return fill(t('duplicates.mergeSet.itemWithheldPick'), { fields: labels.join(', ') });
}

